import { c as _c } from "react/compiler-runtime";
import { postHostedUserDocumentsByIdProcessMutation } from '@onefootprint/fetch-hosted';
import { useRequestError } from '@onefootprint/fetch-hosted/use-request-error';
import type { DocumentResponse } from '@onefootprint/request-types';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { getLogger, trackAction } from '@/idv2/utils/logger';
import { SLOW_CONNECTION_MESSAGE_TIMEOUT } from '../../../constants';
import useSubmitDoc from '../../../hooks/use-submit-doc';
import { IdDocImageUploadError } from '../../../types';
import { bytesToMegabytes } from '../../../utils/capture';
import getBackendErrorMessage from '../../../utils/get-backend-error-message';
import getRejectionCodes from '../../../utils/get-rejection-codes';
import { useNonIdDocMachine } from '../../context/non-id-doc-context';
import MobileProcessingView from './mobile-processing-view';
const {
  logWarn,
  logInfo
} = getLogger({
  location: 'non-id-doc-mobile-processing'
});
const MobileProcessing = () => {
  const $ = _c(46);
  const {
    getErrorMessage,
    getErrorCode
  } = useRequestError();
  const [state, dispatch] = useNonIdDocMachine();
  const submitDocMutation = useSubmitDoc();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = postHostedUserDocumentsByIdProcessMutation();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const processDocMutation = useMutation(t0);
  const [mode, setMode] = useState("loading");
  const [showSlowConnectionMessage, setShowSlowConnectionMessage] = useState(false);
  const [retryLimitExceeded, setRetryLimitExceeded] = useState(false);
  const [isMissingRequirements, setIsMissingRequirements] = useState(false);
  const [step, setStep] = useState("upload");
  const slowConnectionTimer = useRef(null);
  const {
    document,
    authToken,
    id,
    requirement
  } = state.context;
  const {
    kind: documentRequestKind
  } = requirement.config;
  let t1;
  if ($[1] !== documentRequestKind) {
    t1 = () => {
      trackAction(`document-processing-${documentRequestKind}:started`);
    };
    $[1] = documentRequestKind;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  useEffect(t1, t2);
  let t3;
  if ($[4] !== dispatch || $[5] !== documentRequestKind || $[6] !== id) {
    t3 = data => {
      const {
        errorDetails,
        nextSideToCollect,
        isRetryLimitExceeded
      } = data;
      const rejectionCodes = getRejectionCodes(data);
      const rejectionContext = {
        rejectionCodes,
        docKind: documentRequestKind
      };
      if (isRetryLimitExceeded) {
        logWarn(`Image upload retry limit exceeded. doc: ${documentRequestKind}, doc id: ${id}`);
        setRetryLimitExceeded(true);
        return;
      }
      if (nextSideToCollect === "front") {
        logWarn("Document rejected", undefined, rejectionContext);
        trackAction(`document-processing-${documentRequestKind}:rejected`, rejectionContext);
        dispatch({
          type: "processingErrored",
          payload: {
            errors: errorDetails.map(_temp)
          }
        });
        return;
      }
      if (nextSideToCollect) {
        logWarn(`Unexpected next side to collect. Side: ${nextSideToCollect}`);
        return;
      }
      setMode("success");
      trackAction(`document-processing-${documentRequestKind}:completed`);
    };
    $[4] = dispatch;
    $[5] = documentRequestKind;
    $[6] = id;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const handleProcessDocSuccess = t3;
  let t4;
  if ($[8] !== dispatch) {
    t4 = err_0 => {
      const backendMessage = getBackendErrorMessage(err_0);
      dispatch({
        type: "processingErrored",
        payload: {
          errors: [backendMessage ? {
            errorType: "custom_error",
            errorInfo: backendMessage
          } : {
            errorType: IdDocImageUploadError.unknownUploadError
          }]
        }
      });
    };
    $[8] = dispatch;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const handleError = t4;
  let t5;
  if ($[10] !== getErrorMessage || $[11] !== handleError || $[12] !== id) {
    t5 = err_1 => {
      handleError(err_1);
      logWarn(`Error while processing image ${id}: ${getErrorMessage(err_1)}`, err_1);
    };
    $[10] = getErrorMessage;
    $[11] = handleError;
    $[12] = id;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const handleProcessDocError = t5;
  let t6;
  if ($[14] !== documentRequestKind || $[15] !== getErrorCode || $[16] !== getErrorMessage || $[17] !== handleError || $[18] !== id) {
    t6 = err_2 => {
      const errorCode = getErrorCode(err_2);
      const errorMessage = getErrorMessage(err_2);
      if (errorCode === "E109") {
        setMode("success");
        return;
      }
      logWarn(`Submit failed on phone flow. Document kind: ${documentRequestKind}, upload session id: ${id}. Error: ${errorMessage}`, err_2);
      handleError(err_2);
    };
    $[14] = documentRequestKind;
    $[15] = getErrorCode;
    $[16] = getErrorMessage;
    $[17] = handleError;
    $[18] = id;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  const handleSubmitDocError = t6;
  let t7;
  if ($[20] !== authToken || $[21] !== handleProcessDocError || $[22] !== handleProcessDocSuccess || $[23] !== id || $[24] !== processDocMutation) {
    t7 = async () => {
      if (!id) {
        return;
      }
      setStep("analyze");
      ;
      try {
        const response = await processDocMutation.mutateAsync({
          path: {
            id
          },
          headers: {
            "X-Fp-Authorization": authToken
          }
        });
        handleProcessDocSuccess(response);
      } catch (t8) {
        const error = t8;
        handleProcessDocError(error);
      }
    };
    $[20] = authToken;
    $[21] = handleProcessDocError;
    $[22] = handleProcessDocSuccess;
    $[23] = id;
    $[24] = processDocMutation;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  const handleSubmitDocSuccess = t7;
  let t8;
  if ($[26] !== authToken || $[27] !== document || $[28] !== handleSubmitDocError || $[29] !== handleSubmitDocSuccess || $[30] !== id || $[31] !== submitDocMutation) {
    t8 = () => {
      if (!document || !authToken || !id) {
        setIsMissingRequirements(true);
        const error_0 = `Mobile flow - id-doc image could not be processed due to missing requirements. Requirements - document: ${document ? "OK" : "undefined"}, auth token: ${authToken ? "OK" : "undefined"}, id: ${id ? "OK" : "undefined"}`;
        logWarn(error_0);
        return;
      }
      if (!slowConnectionTimer.current) {
        slowConnectionTimer.current = setTimeout(() => {
          if (submitDocMutation.isSuccess) {
            return;
          }
          setShowSlowConnectionMessage(true);
        }, SLOW_CONNECTION_MESSAGE_TIMEOUT);
      }
      const {
        imageFile,
        extraCompressed,
        captureKind
      } = document;
      logInfo(`Uploading image with size ${bytesToMegabytes(imageFile.size)} MB and type ${imageFile.type} in POST request`);
      submitDocMutation.mutate({
        body: {
          fileData: imageFile
        },
        path: {
          id,
          side: "front"
        },
        headers: {
          "X-Fp-Authorization": authToken,
          "X-Fp-Is-Extra-Compressed": String(extraCompressed),
          "X-Fp-Is-Manual": String(captureKind === "manual"),
          "X-Fp-Is-App-Clip": "false",
          "X-Fp-Is-Instant-App": "false",
          "X-Fp-Is-Upload": String(captureKind === "upload"),
          "X-Fp-Is-Forced-Upload": "false"
        }
      }, {
        onSuccess: handleSubmitDocSuccess,
        onError: handleSubmitDocError,
        onSettled: () => {
          if (slowConnectionTimer.current) {
            clearTimeout(slowConnectionTimer.current);
            setShowSlowConnectionMessage(false);
          }
        }
      });
    };
    $[26] = authToken;
    $[27] = document;
    $[28] = handleSubmitDocError;
    $[29] = handleSubmitDocSuccess;
    $[30] = id;
    $[31] = submitDocMutation;
    $[32] = t8;
  } else {
    t8 = $[32];
  }
  let t9;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = [];
    $[33] = t9;
  } else {
    t9 = $[33];
  }
  useEffect(t8, t9);
  let t10;
  if ($[34] !== dispatch) {
    t10 = () => dispatch({
      type: "processingSucceeded"
    });
    $[34] = dispatch;
    $[35] = t10;
  } else {
    t10 = $[35];
  }
  const handleSuccess = t10;
  let t11;
  if ($[36] !== dispatch) {
    t11 = () => dispatch({
      type: "retryLimitExceeded"
    });
    $[36] = dispatch;
    $[37] = t11;
  } else {
    t11 = $[37];
  }
  const handleRetryLimitExceeded = t11;
  let t12;
  if ($[38] !== handleRetryLimitExceeded || $[39] !== handleSuccess || $[40] !== isMissingRequirements || $[41] !== mode || $[42] !== retryLimitExceeded || $[43] !== showSlowConnectionMessage || $[44] !== step) {
    t12 = <MobileProcessingView mode={mode} step={step} showSlowConnectionMessage={showSlowConnectionMessage} retryLimitExceeded={retryLimitExceeded} isMissingRequirements={isMissingRequirements} onSuccess={handleSuccess} onRetryLimitExceeded={handleRetryLimitExceeded} />;
    $[38] = handleRetryLimitExceeded;
    $[39] = handleSuccess;
    $[40] = isMissingRequirements;
    $[41] = mode;
    $[42] = retryLimitExceeded;
    $[43] = showSlowConnectionMessage;
    $[44] = step;
    $[45] = t12;
  } else {
    t12 = $[45];
  }
  return t12;
};
export default MobileProcessing;
function _temp(err) {
  return {
    errorType: err.code ?? "custom_error",
    errorInfo: err.message
  };
}